export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: any,
    public code?: string,
    public requestId?: string,
    public retryable?: boolean
  ) { super(message); }
}

const configuredBase = String(import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
const pendingMutations = new Map<string, Promise<unknown>>();

function requestUrl(path: string) {
  return `${configuredBase}/api${path}`;
}

function mutationKey(path: string, options: RequestInit) {
  const method = String(options.method || "GET").toUpperCase();
  if (["GET", "HEAD", "OPTIONS"].includes(method)) return "";
  if (typeof options.body === "string") return `${method}:${path}:${options.body}`;
  if (options.body instanceof FormData) {
    const parts = Array.from(options.body.entries()).map(([key, value]) => value instanceof File ? `${key}:${value.name}:${value.size}` : `${key}:${value}`);
    return `${method}:${path}:${parts.join("|")}`;
  }
  return `${method}:${path}`;
}

export async function api<T = any>(path: string, options: RequestInit = {}): Promise<T> {
  const key = mutationKey(path, options);
  const existing = key ? pendingMutations.get(key) : undefined;
  if (existing) return existing as Promise<T>;
  const request = (async () => {
    const isForm = options.body instanceof FormData;
    const response = await fetch(requestUrl(path), {
      ...options,
      credentials: "include",
      cache: "no-store",
      headers: { ...(isForm ? {} : { "Content-Type": "application/json" }), ...options.headers }
    });
    const contentType = response.headers.get("content-type") || "";
    const body = contentType.includes("application/json") ? await response.json() : await response.text();
    if (!response.ok) {
      throw new ApiError(
        body?.message || body?.error || "Não foi possível concluir a operação.",
        response.status,
        body?.details ?? (body?.fields ? { fields: body.fields } : undefined),
        body?.message ? body?.error : undefined,
        body?.requestId || response.headers.get("x-request-id") || undefined,
        body?.retryable
      );
    }
    return body as T;
  })();
  if (key) pendingMutations.set(key, request);
  try { return await request; }
  finally { if (key) pendingMutations.delete(key); }
}

export const post = <T = any>(path: string, data: unknown) => api<T>(path, { method: "POST", body: JSON.stringify(data) });
export const patch = <T = any>(path: string, data: unknown) => api<T>(path, { method: "PATCH", body: JSON.stringify(data) });
export const remove = <T = any>(path: string) => api<T>(path, { method: "DELETE" });
